import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatPrice } from "@/lib/api";

/**
 * Add/remove free-text custom line items on a monthly (recurring) invoice.
 * Session line items (auto-generated from the pattern) are read-only here;
 * only items with is_custom=true can be removed.
 */
export default function LineItemsDialog({ registration, onClose, onSaved }) {
  const [items, setItems] = useState(registration.line_items || []);
  const [subtotal, setSubtotal] = useState(Number(registration.subtotal || 0));
  const [cwFee, setCwFee] = useState(Number(registration.classwallet_fee_amount || 0));
  const [total, setTotal] = useState(Number(registration.total || 0));
  const [label, setLabel] = useState("");
  const [subLabel, setSubLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ label: "", sub_label: "", amount: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const customCount = useMemo(
    () => items.filter((li) => li.is_custom).length,
    [items]
  );

  const applyResponse = (data) => {
    if (Array.isArray(data.line_items)) setItems(data.line_items);
    if (data.subtotal != null) setSubtotal(Number(data.subtotal));
    if (data.classwallet_fee_amount != null) setCwFee(Number(data.classwallet_fee_amount));
    if (data.total != null) setTotal(Number(data.total));
  };

  const add = async () => {
    const trimmed = label.trim();
    if (!trimmed) { toast.error("Description is required"); return; }
    const amt = Number(amount);
    if (Number.isNaN(amt) || amt === 0) { toast.error("Enter a non-zero amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/registrations/${registration.id}/line-items`,
        { label: trimmed, amount: amt, sub_label: subLabel.trim() }
      );
      applyResponse(data);
      setLabel("");
      setSubLabel("");
      setAmount("");
      toast.success("Line item added");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add line item");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (li) => {
    setEditingId(li.id);
    setDraft({
      label: li.label || "",
      sub_label: li.sub_label || "",
      amount: String(li.amount ?? ""),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ label: "", sub_label: "", amount: "" });
  };

  // Enter saves. Escape is handled by the Dialog's onEscapeKeyDown instead —
  // Radix listens for it on `document` with capture:true, so a React-level
  // stopPropagation here would run too late to stop the dialog closing.
  const handleEditKey = (e, itemId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit(itemId);
    }
  };

  const saveEdit = async (itemId) => {
    const trimmed = draft.label.trim();
    if (!trimmed) { toast.error("Description is required"); return; }
    const amt = Number(draft.amount);
    if (Number.isNaN(amt) || amt === 0) { toast.error("Enter a non-zero amount"); return; }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(
        `/admin/registrations/${registration.id}/line-items/${itemId}`,
        { label: trimmed, sub_label: draft.sub_label.trim(), amount: amt }
      );
      applyResponse(data);
      cancelEdit();
      toast.success("Line item updated");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update line item");
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async (itemId) => {
    if (!window.confirm("Remove this custom line item?")) return;
    setRemovingId(itemId);
    try {
      const { data } = await api.delete(
        `/admin/registrations/${registration.id}/line-items/${itemId}`
      );
      applyResponse(data);
      toast.success("Line item removed");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove line item");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto"
        data-testid="line-items-dialog"
        onEscapeKeyDown={(e) => {
          // While a row is being edited, Escape abandons that edit only —
          // preventDefault stops Radix from also dismissing the dialog.
          if (editingId) {
            e.preventDefault();
            cancelEdit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Custom line items</DialogTitle>
          <DialogDescription>
            Invoice #{registration.invoice_number} · {registration.student_name}. Add a free-text charge (e.g. supplies fee, curriculum book, make-up session, or a negative amount for a discount). Totals auto-update; the next PDF download reflects your changes.
          </DialogDescription>
        </DialogHeader>

        {/* Existing line items */}
        <div className="rounded-xl border border-gg-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gg-bg text-gg-brown text-xs uppercase tracking-[0.14em]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Description</th>
                <th className="text-right px-3 py-2 font-semibold">Amount</th>
                <th className="text-right px-3 py-2 font-semibold w-16">—</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={3} className="px-3 py-4 text-gg-muted italic">No line items yet.</td></tr>
              ) : (
                items.map((li, idx) => (
                  <tr
                    key={li.id || `s-${idx}`}
                    className={`border-t border-gg-border ${li.is_custom ? "bg-gg-green/5" : ""}`}
                    data-testid={li.is_custom ? `line-item-custom-${li.id}` : `line-item-session-${idx}`}
                  >
                    {editingId === li.id ? (
                      <>
                        <td className="px-3 py-2 space-y-1.5">
                          <Input
                            value={draft.label}
                            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                            placeholder="Description"
                            maxLength={200}
                            autoFocus
                            className="h-8 text-sm"
                            data-testid={`line-item-edit-label-${li.id}`}
                            onKeyDown={(e) => handleEditKey(e, li.id)}
                          />
                          <Input
                            value={draft.sub_label}
                            onChange={(e) => setDraft({ ...draft, sub_label: e.target.value })}
                            placeholder="Subtext (optional)"
                            maxLength={200}
                            className="h-8 text-xs"
                            data-testid={`line-item-edit-sub-label-${li.id}`}
                            onKeyDown={(e) => handleEditKey(e, li.id)}
                          />
                        </td>
                        <td className="px-3 py-2 text-right align-top">
                          <Input
                            type="number"
                            step="0.01"
                            value={draft.amount}
                            onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                            className="h-8 text-sm text-right font-mono"
                            data-testid={`line-item-edit-amount-${li.id}`}
                            onKeyDown={(e) => handleEditKey(e, li.id)}
                          />
                        </td>
                        <td className="px-3 py-2 text-right align-top">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => saveEdit(li.id)}
                              disabled={savingEdit}
                              className="text-gg-green hover:bg-gg-green/10 rounded-full p-1.5 disabled:opacity-40"
                              data-testid={`line-item-save-${li.id}`}
                              title="Save changes"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={savingEdit}
                              className="text-gg-muted hover:bg-gg-border/40 rounded-full p-1.5 disabled:opacity-40"
                              data-testid={`line-item-cancel-${li.id}`}
                              title="Cancel"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2">
                          <div className={li.is_custom ? "font-semibold" : ""}>{li.label || "Session"}</div>
                          <div className="text-xs text-gg-muted">
                            {li.is_custom
                              ? (li.sub_label || "Custom charge")
                              : [li.date, li.time].filter(Boolean).join(" · ") || "Session"}
                          </div>
                        </td>
                        <td className={`px-3 py-2 text-right font-mono ${Number(li.amount) < 0 ? "text-[#B24C4C]" : ""}`}>
                          {formatPrice(Number(li.amount || 0))}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {li.is_custom ? (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => startEdit(li)}
                                className="text-gg-brown hover:bg-gg-brown/10 rounded-full p-1.5"
                                data-testid={`line-item-edit-${li.id}`}
                                title="Edit this line item"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => remove(li.id)}
                                disabled={removingId === li.id}
                                className="text-[#B24C4C] hover:bg-[#B24C4C]/10 rounded-full p-1.5 disabled:opacity-40"
                                data-testid={`line-item-remove-${li.id}`}
                                title="Remove custom line item"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-[10px] text-gg-muted">auto</span>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-gg-bg text-sm">
              <tr className="border-t border-gg-border">
                <td className="px-3 py-2 text-right text-gg-muted">Subtotal</td>
                <td className="px-3 py-2 text-right font-mono" data-testid="line-items-subtotal">{formatPrice(subtotal)}</td>
                <td />
              </tr>
              {cwFee > 0 && (
                <tr>
                  <td className="px-3 py-2 text-right text-gg-muted">ClassWallet fee</td>
                  <td className="px-3 py-2 text-right font-mono" data-testid="line-items-cw-fee">{formatPrice(cwFee)}</td>
                  <td />
                </tr>
              )}
              <tr className="border-t border-gg-border">
                <td className="px-3 py-2 text-right font-semibold text-[var(--gg-text)]">Total</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-gg-green" data-testid="line-items-total">{formatPrice(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Add new */}
        <div className="rounded-xl border border-gg-border bg-gg-bg p-3 mt-3">
          <div className="text-xs uppercase tracking-[0.14em] text-gg-brown font-semibold mb-2">Add a line item</div>
          <div className="grid grid-cols-[1fr_140px_auto] gap-2 items-end">
            <div>
              <Label className="text-xs">Description</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Curriculum book — Botany, Grade 3"
                className="mt-1"
                maxLength={200}
                data-testid="line-item-label-input"
                onKeyDown={(e) => e.key === "Enter" && add()}
              />
              <Label className="text-xs mt-2 block">Subtext (optional)</Label>
              <Input
                value={subLabel}
                onChange={(e) => setSubLabel(e.target.value)}
                placeholder="e.g. Ordered Aug 3 · non-refundable"
                className="mt-1"
                maxLength={200}
                data-testid="line-item-sub-label-input"
                onKeyDown={(e) => e.key === "Enter" && add()}
              />
            </div>
            <div>
              <Label className="text-xs">Amount (USD)</Label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="mt-1"
                data-testid="line-item-amount-input"
                onKeyDown={(e) => e.key === "Enter" && add()}
              />
            </div>
            <button
              type="button"
              onClick={add}
              disabled={busy}
              className="btn-pill btn-primary text-xs disabled:opacity-60"
              data-testid="line-item-add-btn"
            >
              <Plus size={12} /> {busy ? "Adding…" : "Add"}
            </button>
          </div>
          <p className="text-[11px] text-gg-muted mt-2">
            Use a negative amount for a discount or credit (e.g. <code>-25.00</code>). Subtext appears as small italic detail under the description on the PDF. {customCount} custom item{customCount !== 1 ? "s" : ""} on this invoice.
          </p>
        </div>

        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline" data-testid="line-items-close-btn">
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
